import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import type { EventSeriesConflict, EventSeriesPreview } from '../../types/calendar';
import { requestErrorMessage } from '../../utils/saveErrors';
import { describeSeriesConflicts, formatSeriesDay, seriesConflictsFromError } from '../../utils/eventSeries';

/**
 * [magánóra-sorozat, 2026-10-31] KÖZÖS sorozat-előnézet a létrehozóhoz (`EventCreateDialog`) és a bővítéshez
 * (`EventEditDialog`) — egy példány, nem kettő.
 *
 * MI VOLT A BAJ: az előnézet eddig a KLIENS saját dátum-számításából jött (`utils/recurringSeries.ts`
 * `recurringEventDates` / `seriesExtensionDates`), az ütközést pedig csak a mentés derítette ki, alkalmanként
 * (részleges sorozat). A klub döntése (2026-10-31, 7. pont): ha BÁRMELYIK nap ütközik, a sorozat NEM jön
 * létre. Ezért az előnézet a szerver `dryRun` válaszából jön, és ütközésnél a mentés tiltva.
 *
 * KÉSLELTETÉS (debounce): a kérés csak a beállítások `SERIES_PREVIEW_DELAY_MS` ideig tartó nyugalma után
 * megy ki — nem minden leütésre. ELAVULT VÁLASZ: minden új beállítás új sorszámot kap; a régebbi kérés
 * válasza (ha később érkezik) eldobódik, nem írhatja felül az újabbat.
 */
export const SERIES_PREVIEW_DELAY_MS = 500;

export type SeriesPreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; preview: EventSeriesPreview }
  | { status: 'error'; message: string; /** 400 = bemeneti hiba (pl. 26 alkalom fölött) → a mentés is tiltva. */ invalid: boolean; conflicts: EventSeriesConflict[] };

/**
 * `payload === null` → nincs (még) mit előnézni (hiányos űrlap), `idle`. A payload tartalma (JSON) a kulcs:
 * azonos tartalmú új objektum nem indít új kérést.
 */
export function useSeriesPreview<T>(
  payload: T | null,
  fetchPreview: (payload: T) => Promise<EventSeriesPreview>,
  delayMs: number = SERIES_PREVIEW_DELAY_MS,
): SeriesPreviewState {
  const key = payload == null ? null : JSON.stringify(payload);
  const fetchRef = useRef(fetchPreview);
  fetchRef.current = fetchPreview;
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const seq = useRef(0);
  const [state, setState] = useState<SeriesPreviewState>({ status: 'idle' });

  useEffect(() => {
    const mine = ++seq.current;
    if (key == null) {
      setState({ status: 'idle' });
      return;
    }
    setState({ status: 'loading' });
    const timer = setTimeout(async () => {
      try {
        const preview = await fetchRef.current(payloadRef.current as T);
        if (mine === seq.current) setState({ status: 'ready', preview });
      } catch (err: any) {
        if (mine !== seq.current) return;
        setState({
          status: 'error',
          message: requestErrorMessage(err, 'Az előnézet nem tölthető be'),
          invalid: err?.response?.status === 400,
          conflicts: seriesConflictsFromError(err),
        });
      }
    }, delayMs);
    return () => {
      clearTimeout(timer);
      // A futó (már elküldött) kérés válasza ezután elavult — a következő futás új sorszámot kap.
      seq.current++; // eslint-disable-line react-hooks/exhaustive-deps -- szándékos: számláló, nem DOM-ref
    };
  }, [key, delayMs]);

  return state;
}

/**
 * Tiltja-e az előnézet a mentést: ütközés (`canCreate: false`), üres eredmény, vagy a szerver 400-as bemeneti
 * hibája (pl. 26 alkalom fölött). Betöltés közben és hálózati hibánál NEM tilt: a mentést a szerver úgyis
 * ugyanígy ellenőrzi (minden-vagy-semmi), tehát hibás sorozat így sem jöhet létre.
 */
export function seriesPreviewBlocksSave(state: SeriesPreviewState): boolean {
  if (state.status === 'ready') return !state.preview.canCreate || state.preview.count === 0;
  if (state.status === 'error') return state.invalid;
  return false;
}

/** Az ütköző napok listája (az előnézetben és a mentés 400-as válaszánál is). */
export const SeriesConflictAlert: React.FC<{ conflicts: EventSeriesConflict[]; testId?: string }> = ({ conflicts, testId }) => (
  <Alert severity="error" data-testid={testId ?? 'series-conflicts'}>
    {describeSeriesConflicts(conflicts)}
  </Alert>
);

interface SeriesPreviewAlertProps {
  state: SeriesPreviewState;
  /** Mit írjunk, amíg nincs mit előnézni (hiányos űrlap). */
  idleHint: string;
  /** Bővítésnél: a meglévő alkalmak száma (azok változatlanok maradnak). */
  existingCount?: number;
  testId: string;
  /**
   * Mit tegyen a felhasználó, ha az előnézet a mentést TILTJA (400 / 0 új nap) — pl. a szerkesztőben: „Ha nem akarsz
   * bővíteni, kapcsold ki a sorozat bővítését." [1. web-validátor M1]: a 0 új napos bővítés a TELJES mentést tiltja.
   */
  blockedHint?: string;
}

export const SeriesPreviewAlert: React.FC<SeriesPreviewAlertProps> = ({ state, idleHint, existingCount, testId, blockedHint }) => {
  const hint = blockedHint && seriesPreviewBlocksSave(state)
    ? <Typography variant="body2" data-testid={`${testId}-blocked-hint`}>{blockedHint}</Typography>
    : null;
  if (state.status === 'idle') {
    return <Alert severity="info" data-testid={testId} data-status="idle">{idleHint}</Alert>;
  }
  if (state.status === 'loading') {
    return <Alert severity="info" data-testid={testId} data-status="loading">Előnézet számítása…</Alert>;
  }
  if (state.status === 'error') {
    return (
      <Box data-testid={testId} data-status="error" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Alert severity="error">{state.message}</Alert>
        {state.conflicts.length > 0 && <SeriesConflictAlert conflicts={state.conflicts} />}
        {hint}
      </Box>
    );
  }
  const { preview } = state;
  if (preview.conflicts.length > 0) {
    return (
      <Box data-testid={testId} data-status="conflict" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <SeriesConflictAlert conflicts={preview.conflicts} />
        {hint}
      </Box>
    );
  }
  if (preview.count === 0) {
    return (
      <Alert severity="warning" data-testid={testId} data-status="empty">
        Ezekkel a beállításokkal nem jön létre új alkalom (a napok már léteznek, vagy a múltban vannak).
        {hint}
      </Alert>
    );
  }
  const dates = preview.eventDates;
  return (
    <Alert severity="success" data-testid={testId} data-status="ready">
      <Typography variant="body2" component="div">
        {`${preview.count} alkalom jön létre (${formatSeriesDay(dates[0])} – ${formatSeriesDay(dates[dates.length - 1])}).`}
        {existingCount != null ? ` A meglévő ${existingCount} alkalom változatlan marad.` : ''}
      </Typography>
      <Typography variant="caption" component="div" data-testid={`${testId}-dates`}>
        {dates.map(d => formatSeriesDay(d)).join(', ')}
      </Typography>
    </Alert>
  );
};
